import { PaymentMethod, TemporaryBillsOrInvoicesRails } from 'vopay';
import { useTranslation } from 'react-i18next';
import { useApi } from '#shared/providers/Api';
import { useToast } from '#shared/providers/Toast';
import { useAccountingSoftware } from './AccountingSoftwareContext';
import { useBlockingLoader } from '#shared/providers/Loader';
import usePaymentMethodOps from './usePaymentMethodOps';
import { getAspRowExtras } from './aspAccountingAdapter';
import { PaymentUpdate } from './AccountingSystemOpen/types';

function findExisting(existingMethods: PaymentMethod[], type: PaymentMethod['PaymentMethod']) {
  const ofType = existingMethods.filter((method) => method.PaymentMethod === type);

  return ofType.find((method) => method.IsDefault) || ofType[0];
}

// Shared save-flow for the Banking Update dialogs on asp (Receivable) rows.
// Persists the edited method onto the contact's client account, where the
// server-side pay pipeline reads it: create → set as default → delete the
// replaced method (edits are replace, there is no in-place update API).
// Adds go through the same paths as the Single Payment page (postIQ11Tokenize
// / addInterac) and default-setting through usePaymentMethods, as on the
// customer details Payment Methods tab.
// Returns true when the method was persisted (or the row/values need no
// persistence), false when the save failed and the dialog should stay open.
export default function usePersistAspPaymentMethod() {
  const api = useApi();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.OpenTab.Utils' });
  const { account: response } = useAccountingSoftware();
  const clientAccountsEnabled = Boolean(response.ClientAccountsEnabled);
  const blockingLoader = useBlockingLoader();
  const { addInterac, setDefaultMethod, deleteMethod } = usePaymentMethodOps();

  async function persistMethod(
    contactId: string,
    customerName: string,
    values: PaymentUpdate,
    existingMethods: PaymentMethod[],
  ) {
    const method = values.PaymentMethod;

    if (method === 'Bank Account' || method === 'RTP' || method === 'VoPay Instant') {
      const tokenRes = await api.postIQ11Tokenize({
        PortalUser: response.LoginUserName,
        AccountHolderName: customerName,
        Currency: response.Country === 'CA' ? 'CAD' : 'USD',
        AccountNumber: values.AccountNumber,
        FinancialInstitutionNumber: values.FinancialInstitutionNumber,
        BranchTransitNumber: values.BankTransitNumber,
        ABARoutingNumber: values.RoutingNumber,
        ClientControlled: clientAccountsEnabled,
        [clientAccountsEnabled ? 'ClientAccountID' : 'ContactID']: contactId,
        Address1: values.Address,
        City: values.City,
        Province: values.State,
        PostalCode: values.ZipCode,
        PhoneNumber: values.PhoneNumber,
        Country: response.Country,
      });

      if (!tokenRes.Success) throw new Error(tokenRes.ErrorMessage);

      const setRes = await setDefaultMethod('BankAccount', tokenRes.Token, contactId);
      if (!setRes.Success) throw new Error(setRes.ErrorMessage);

      const original = findExisting(existingMethods, 'BankAccount');

      if (original && original.Token !== tokenRes.Token) {
        // Deleting the replaced method is cleanup — a failure (e.g. already
        // deleted) must not abort a save whose new default is already set.
        await Promise.resolve(deleteMethod(original, contactId)).catch(console.error);
      }

      return;
    }

    if (method === 'Interac e-Transfer' || method === 'Interac e-Transfer Money Request') {
      const isEmail = values.InteracMethod !== 'Phone Number';

      const addRes = await addInterac({
        accountID: contactId,
        recipientName: customerName,
        email: isEmail ? values.Email || '' : '',
        phone: isEmail ? '' : values.Phone || '',
        question: values.Question || '',
        answer: values.Answer || '',
      });

      if (!addRes.Success) throw new Error(addRes.ErrorMessage);

      const setRes = await setDefaultMethod('Email', addRes.Token, contactId);
      if (!setRes.Success) throw new Error(setRes.ErrorMessage);

      const original =
        findExisting(existingMethods, isEmail ? 'Email' : 'PhoneNumber') ||
        findExisting(existingMethods, isEmail ? 'PhoneNumber' : 'Email');

      if (original && original.Token !== addRes.Token) {
        await Promise.resolve(deleteMethod(original, contactId)).catch(console.error);
      }

      return;
    }

    // Cards arrive already tokenized (iframe flows) — attach the token to the
    // contact as its default; nothing to create or delete here. A missing token
    // means the iframe never handed one back, so fail rather than fall through
    // and report a save that never happened.
    if (method === 'Credit Card' || method === 'Debit Card') {
      const isCredit = method === 'Credit Card';
      const token = isCredit ? values.TokenCredit : values.TokenDebit;

      if (!token) {
        throw new Error(
          t('missingCardTokenError', {
            defaultValue: 'The card was not tokenized. Please provide the card details again.',
          }),
        );
      }

      const res = await setDefaultMethod(isCredit ? 'CreditCard' : 'DebitCard', token, contactId);
      if (!res.Success) throw new Error(res.ErrorMessage);
    }
  }

  async function persistPaymentMethod(row: TemporaryBillsOrInvoicesRails, values: PaymentUpdate): Promise<boolean> {
    const extras = getAspRowExtras(row);
    // Not an asp row (legacy flow carries banking data in the pay payload) or
    // an eLinx request (the recipient provides their own details).
    if (!extras || values.IsManualEntry === false) return true;

    if (!extras.AspClientAccountID) {
      toast.neutral(
        t('noClientAccountError', {
          defaultValue: "This customer has no linked client account, so the payment method can't be saved.",
        }),
      );

      return false;
    }

    try {
      blockingLoader.show();

      await persistMethod(
        extras.AspClientAccountID,
        row.Customer?.Name || row.customerRef?.name || '',
        values,
        extras.AspPaymentMethods,
      );

      return true;
    } catch (error) {
      console.error(error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('savePaymentMethodError', {
          defaultValue: 'Could not save the payment method: {{errorMessage}}',
          errorMessage,
        }),
      );

      return false;
    } finally {
      blockingLoader.hide();
    }
  }

  return { persistPaymentMethod };
}
